/**
 * compute.ts — pure local-widget data derivation.
 *
 * Produces the normalized data shapes for note.sticky, todo.list, and
 * countdown.event from their params + the current time. Zero side effects,
 * zero network. Mirrors how clock.local computes from `new Date()`.
 *
 * TIME MATH NOTE — UTC-based whole-day diff for countdown:
 * We compute daysLeft via whole-UTC-day arithmetic:
 *   Math.floor((targetMs - nowMs) / MS_PER_DAY)
 * Rationale: a "day" here is a 24h period, not a calendar day in local time.
 * This avoids DST ambiguity: on a spring-forward day a local calendar diff
 * would produce a fractional day; dividing raw milliseconds by 86400000 and
 * flooring gives the WHOLE number of 24h periods remaining, which is the
 * correct reading of "N days until" on a countdown tile. Past targets yield a
 * negative value (daysLeft < 0), matching sinceOrUntil === 'since'. hoursLeft
 * is the total whole hours (floored), also negative when past.
 *
 * Immutability: all returned objects are new; inputs are never mutated.
 */

import type { NoteParams, TodoParams, CountdownParams, TodoItem } from 'briefpane/schema';
import { MAX_NOTE_CHARS } from 'briefpane/schema';

const MS_PER_HOUR = 3_600_000;
const MS_PER_DAY = 24 * MS_PER_HOUR;

/** Max preview chars for note primary. Spec: "~80 chars, single line". */
const NOTE_PREVIEW_CHARS = 80;

/* ---- note.sticky --------------------------------------------------------- */

export interface NoteData {
  readonly preview: string;
  readonly charCount: number;
}

/**
 * Derive NoteData from NoteParams.
 * preview: first NOTE_PREVIEW_CHARS chars of text with newlines collapsed to
 * spaces. Empty string when text is '' (fresh note).
 * charCount: raw character count, 0–MAX_NOTE_CHARS.
 */
export function computeNoteData(params: NoteParams): NoteData {
  const text = params.text ?? '';
  // Collapse newlines → spaces, then trim to preview length.
  const singleLine = text.replace(/\n/g, ' ');
  const preview = singleLine.slice(0, NOTE_PREVIEW_CHARS);
  return {
    preview,
    charCount: Math.min(text.length, MAX_NOTE_CHARS),
  };
}

/* ---- todo.list ----------------------------------------------------------- */

export interface TodoData {
  readonly doneCount: number;
  readonly totalCount: number;
  readonly remainingCount: number;
  readonly nextItemText: string;
}

/**
 * Derive TodoData from TodoParams.
 * nextItemText: text of the FIRST not-done item in array order; '' if none.
 * Order is preserved (the array order IS the user's prioritization, per schema).
 */
export function computeTodoData(params: TodoParams): TodoData {
  const items: readonly TodoItem[] = params.items ?? [];
  const totalCount = items.length;
  const doneCount = items.filter((i) => i.done).length;
  const remainingCount = totalCount - doneCount;
  const firstUndone = items.find((i) => !i.done);
  const nextItemText = firstUndone?.text ?? '';
  return {
    doneCount,
    totalCount,
    remainingCount,
    nextItemText,
  };
}

/* ---- countdown.event ----------------------------------------------------- */

export interface CountdownData {
  readonly targetLabel: string;
  readonly daysLeft: number;
  readonly hoursLeft: number;
  readonly sinceOrUntil: 'until' | 'since';
}

/**
 * Derive CountdownData from CountdownParams and the current time.
 *
 * Time math: UTC-based whole-24h-period arithmetic (see module docblock).
 * daysLeft  = Math.floor((targetMs - nowMs) / MS_PER_DAY)  [negative when past]
 * hoursLeft = Math.floor((targetMs - nowMs) / MS_PER_HOUR) [negative when past]
 * sinceOrUntil: 'until' when target is in the future (diff > 0), 'since' otherwise.
 *
 * NOTE: daysLeft is 0 on the day of the event (less than 24h remaining), and
 * -1 the day after, etc. This matches the countdown-tile UX: "Today" is
 * represented by 0d (or hours-based if < 24h) rather than rounding up.
 */
export function computeCountdownData(params: CountdownParams, now: Date): CountdownData {
  const targetMs = Date.parse(params.targetISO);
  const nowMs = now.getTime();
  const diffMs = targetMs - nowMs;

  // Whole 24h periods (floor, not round: always towards zero when positive)
  const daysLeft = Math.floor(diffMs / MS_PER_DAY);
  const hoursLeft = Math.floor(diffMs / MS_PER_HOUR);
  const sinceOrUntil: 'until' | 'since' = diffMs > 0 ? 'until' : 'since';

  return {
    targetLabel: params.label,
    daysLeft,
    hoursLeft,
    sinceOrUntil,
  };
}
